import { useMemo } from "react"

import {
    QueryClient,
    type QueryKey,
    type UseBaseQueryOptions,
    type UseQueryResult,
    type UseSuspenseQueryResult,
    useQuery,
    useQueryClient,
    useSuspenseQuery
} from "@tanstack/react-query"
import { AxiosError } from "axios"
import find from "lodash-es/find"
import isUndefined from "lodash-es/isUndefined"

import { coachingSessionAdapter, coachingSessionsAdapter } from "@/3514/adapters"
import { ECoachingSessionsGroupQueryKey, ECoachingSessionsIndividualQueryKey } from "@/3514/config"
import { coachingSessionsGroupFetcher, coachingSessionsIndividualFetcher } from "@/3514/fetchers"
import type {
    ICoachingSessionDto,
    ICoachingSessionModel,
    ICoachingSessionsQueryDto,
    ICoachingSessionsQueryModel
} from "@/3514/types"
import { minutes, seconds } from "@/3514/utils/time.utils"

type _TCoachingSessionsSuspenseQueryOptions = Pick<
    UseBaseQueryOptions,
    "refetchInterval" | "refetchOnWindowFocus" | "refetchOnMount"
> & { participantId: number }
type _TCoachingSessionsIndividualQueryOptions = Pick<
    UseBaseQueryOptions,
    "refetchInterval" | "refetchOnWindowFocus" | "refetchOnMount" | "enabled"
> & { participantId: number }
type _TCoachingSessionsGroupSuspenseQueryOptions = Pick<
    UseBaseQueryOptions,
    "refetchInterval" | "refetchOnWindowFocus" | "refetchOnMount"
> & { groupId: number }
type _TCoachingSessionsGroupQueryOptions = Pick<
    UseBaseQueryOptions,
    "refetchInterval" | "refetchOnWindowFocus" | "refetchOnMount" | "enabled"
> & { groupId: number }

type TCoachingSessionsIndividualSuspenseQuery = UseSuspenseQueryResult<ICoachingSessionsQueryModel, AxiosError>
type TCoachingSessionsGroupSuspenseQuery = UseSuspenseQueryResult<ICoachingSessionsQueryModel, AxiosError>
type TCoachingSessionsIndividualQuery = UseQueryResult<ICoachingSessionsQueryModel, AxiosError>
type TCoachingSessionsGroupQuery = UseQueryResult<ICoachingSessionsQueryModel, AxiosError>
/**
 * @deprecated backward-compatibility hook used to only handle old accordions stuff
 */
type TLegacyCoachingSessionQuery = UseQueryResult<ICoachingSessionsQueryDto>

function useCoachingSessionsIndividualSuspenseQuery(
    params: _TCoachingSessionsSuspenseQueryOptions
): ReturnType<(params: _TCoachingSessionsSuspenseQueryOptions) => TCoachingSessionsIndividualSuspenseQuery> {
    const _defaultOptions: Partial<_TCoachingSessionsSuspenseQueryOptions> = {
        refetchInterval: undefined,
        refetchOnWindowFocus: undefined,
        refetchOnMount: undefined
    }

    const options: _TCoachingSessionsSuspenseQueryOptions = {
        ..._defaultOptions,
        ...params
    }

    return useSuspenseQuery<ICoachingSessionsQueryDto, AxiosError, ICoachingSessionsQueryModel, QueryKey>({
        queryKey: ECoachingSessionsIndividualQueryKey({ participantId: options.participantId }),
        queryFn: coachingSessionsIndividualFetcher,
        select: coachingSessionsAdapter,
        staleTime: isUndefined(options.refetchInterval) ? minutes(15) : Number(options.refetchInterval),
        gcTime: isUndefined(options.refetchInterval) ? minutes(15) : Number(options.refetchInterval),
        refetchOnWindowFocus: options.refetchOnWindowFocus,
        refetchOnMount: options.refetchOnMount,
        refetchInterval: isUndefined(options.refetchInterval) ? minutes(15) : options.refetchInterval,
        refetchIntervalInBackground: true,
        retry: 1,
        retryDelay: seconds(3),
        networkMode: "offlineFirst",
        notifyOnChangeProps: ["data", "dataUpdatedAt", "isSuccess"]
    })
}

function useCoachingSessionsIndividualQuery(
    params: _TCoachingSessionsIndividualQueryOptions
): ReturnType<(params: _TCoachingSessionsIndividualQueryOptions) => TCoachingSessionsIndividualQuery> {
    const _defaultOptions: Partial<_TCoachingSessionsIndividualQueryOptions> = {
        refetchInterval: undefined,
        refetchOnWindowFocus: undefined,
        refetchOnMount: undefined
    }

    const options: _TCoachingSessionsIndividualQueryOptions = {
        ..._defaultOptions,
        ...params
    }

    return useQuery<ICoachingSessionsQueryDto, AxiosError, ICoachingSessionsQueryModel, QueryKey>({
        queryKey: ECoachingSessionsIndividualQueryKey({ participantId: options.participantId }),
        queryFn: coachingSessionsIndividualFetcher,
        select: coachingSessionsAdapter,
        staleTime: isUndefined(options.refetchInterval) ? minutes(15) : Number(options.refetchInterval),
        gcTime: isUndefined(options.refetchInterval) ? minutes(15) : Number(options.refetchInterval),
        refetchOnWindowFocus: options.refetchOnWindowFocus,
        refetchOnMount: options.refetchOnMount,
        refetchInterval: isUndefined(options.refetchInterval) ? minutes(15) : options.refetchInterval,
        refetchIntervalInBackground: true,
        retry: 1,
        retryDelay: seconds(3),
        networkMode: "offlineFirst",
        notifyOnChangeProps: ["data", "dataUpdatedAt", "isSuccess"],
        enabled: options.enabled,
        experimental_prefetchInRender: true,
        throwOnError: false
    })
}

function useCoachingSessionsGroupSuspenseQuery(
    params: _TCoachingSessionsGroupSuspenseQueryOptions
): ReturnType<(params: _TCoachingSessionsGroupSuspenseQueryOptions) => TCoachingSessionsGroupSuspenseQuery> {
    return useSuspenseQuery<ICoachingSessionsQueryDto, AxiosError, ICoachingSessionsQueryModel, QueryKey>({
        queryKey: ECoachingSessionsGroupQueryKey({ groupId: params.groupId }),
        queryFn: coachingSessionsGroupFetcher,
        select: coachingSessionsAdapter,
        staleTime: isUndefined(params.refetchInterval) ? minutes(15) : Number(params.refetchInterval),
        gcTime: isUndefined(params.refetchInterval) ? minutes(15) : Number(params.refetchInterval),
        refetchOnWindowFocus: params.refetchOnWindowFocus,
        refetchOnMount: params.refetchOnMount,
        refetchInterval: isUndefined(params.refetchInterval) ? minutes(15) : params.refetchInterval,
        refetchIntervalInBackground: true,
        retry: 1,
        retryDelay: seconds(3),
        networkMode: "offlineFirst",
        notifyOnChangeProps: ["data", "dataUpdatedAt", "isSuccess"]
    })
}

function useCoachingSessionsGroupQuery(
    params: _TCoachingSessionsGroupQueryOptions
): ReturnType<(params: _TCoachingSessionsGroupQueryOptions) => TCoachingSessionsGroupQuery> {
    return useQuery<ICoachingSessionsQueryDto, AxiosError, ICoachingSessionsQueryModel, QueryKey>({
        queryKey: ECoachingSessionsGroupQueryKey({ groupId: params.groupId }),
        queryFn: coachingSessionsGroupFetcher,
        select: coachingSessionsAdapter,
        staleTime: isUndefined(params.refetchInterval) ? minutes(15) : Number(params.refetchInterval),
        gcTime: isUndefined(params.refetchInterval) ? minutes(15) : Number(params.refetchInterval),
        refetchOnWindowFocus: params.refetchOnWindowFocus,
        refetchOnMount: params.refetchOnMount,
        refetchInterval: isUndefined(params.refetchInterval) ? minutes(15) : params.refetchInterval,
        refetchIntervalInBackground: true,
        retry: 1,
        retryDelay: seconds(3),
        networkMode: "offlineFirst",
        notifyOnChangeProps: ["data", "dataUpdatedAt", "isFetching", "isError", "isSuccess"],
        enabled: params.enabled,
        throwOnError: true,
        experimental_prefetchInRender: true
    })
}

/**
 * @deprecated backward-compatibility hook used to only handle old accordions stuff
 */
function useLegacyCoachingSessionsQuery(
    options: _TCoachingSessionsIndividualQueryOptions = {
        refetchInterval: undefined,
        refetchOnWindowFocus: undefined,
        refetchOnMount: undefined,
        enabled: false,
        participantId: undefined
    }
): ReturnType<(options: _TCoachingSessionsIndividualQueryOptions) => TLegacyCoachingSessionQuery> {
    return useQuery<ICoachingSessionsQueryDto, AxiosError, ICoachingSessionsQueryDto, QueryKey>({
        queryKey: ECoachingSessionsIndividualQueryKey({ participantId: options.participantId }),
        queryFn: coachingSessionsIndividualFetcher,
        staleTime: isUndefined(options.refetchInterval) ? minutes(15) : Number(options.refetchInterval),
        gcTime: isUndefined(options.refetchInterval) ? minutes(15) : Number(options.refetchInterval),
        refetchOnWindowFocus: options.refetchOnWindowFocus,
        refetchOnMount: options.refetchOnMount,
        refetchInterval: isUndefined(options.refetchInterval) ? minutes(15) : options.refetchInterval,
        refetchIntervalInBackground: true,
        retry: 1,
        retryDelay: seconds(3),
        networkMode: "offlineFirst",
        notifyOnChangeProps: ["data", "isFetching", "isFetched", "refetch"],
        enabled: options.enabled,
        throwOnError: false
    })
}

function useGetCachedCoachingSessionById(
    { id }: { id: number },
    { queryKey }: { queryKey: QueryKey }
): ICoachingSessionModel | undefined {
    const qc: QueryClient = useQueryClient()

    return useMemo<ICoachingSessionModel | undefined>((): ICoachingSessionModel | undefined => {
        const queryDto: ICoachingSessionsQueryDto | undefined = qc.getQueryData<ICoachingSessionsQueryDto>(queryKey)

        const sessionDto: ICoachingSessionDto | undefined = !!queryDto
            ? find<ICoachingSessionDto>(
                  [...queryDto.not_scheduled_sessions, ...queryDto.passed_sessions, ...queryDto.upcoming_sessions],
                  { session_id: id }
              )
            : undefined

        return !!sessionDto ? coachingSessionAdapter(sessionDto) : undefined
    }, [qc, id, queryKey])
}

async function coachingSessionsIndividualQueryPrefetcher(
    { qc }: { qc: QueryClient },
    { participantId }: { participantId?: number }
): Promise<void> {
    await qc.prefetchQuery<ICoachingSessionsQueryDto, AxiosError, ICoachingSessionsQueryModel, QueryKey>({
        queryKey: ECoachingSessionsIndividualQueryKey({ participantId }),
        queryFn: coachingSessionsIndividualFetcher,
        staleTime: minutes(5),
        gcTime: minutes(5)
    })
}

async function coachingSessionsGroupQueryPrefetcher(
    { qc }: { qc: QueryClient },
    { groupId }: { groupId: number }
): Promise<void> {
    await qc.prefetchQuery<ICoachingSessionsQueryDto, AxiosError, ICoachingSessionsQueryModel, QueryKey>({
        queryKey: ECoachingSessionsGroupQueryKey({ groupId }),
        queryFn: coachingSessionsGroupFetcher,
        staleTime: minutes(5),
        gcTime: minutes(5)
    })
}

export {
    useCoachingSessionsIndividualSuspenseQuery,
    useCoachingSessionsGroupSuspenseQuery,
    useCoachingSessionsGroupQuery,
    useCoachingSessionsIndividualQuery,
    useLegacyCoachingSessionsQuery,
    useGetCachedCoachingSessionById,
    coachingSessionsIndividualQueryPrefetcher,
    coachingSessionsGroupQueryPrefetcher
}
export type {
    TCoachingSessionsIndividualSuspenseQuery,
    TCoachingSessionsGroupSuspenseQuery,
    TCoachingSessionsIndividualQuery,
    TCoachingSessionsGroupQuery,
    TLegacyCoachingSessionQuery
}
